import { AlertCircle, Plus, ChevronDownCircle } from 'lucide-react';
import Header from '../components/Header';
import { CategoryList } from '../components/CategoryList';
import { usePlans } from '../contexts/PlanContext';
import { useCategories } from '../contexts/CategoryContext';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlanModal } from '../components/PlanModal';
import { CategoryModal } from '../components/CategoryModal';
import { MonthCalendarPicker } from '../components/MonthCalendarPicker';

export default function BudgetPage() {
  const { t } = useTranslation();
  const { currentPlan, currentMonthPlan, allMonthPlans, loadMonthPlanByDate, createPlan, loading } = usePlans();
  const { createCategory } = useCategories();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  const handleCreatePlan = async (name: string) => {
    await createPlan({ name });
    setShowCreateModal(false);
  };

  const handleAddGroup = () => {
    setShowAddGroupModal(true);
  };

  const handleSaveGroup = async (name: string) => {
    if (!currentPlan || !currentMonthPlan) throw new Error('No active plan or month plan');

    await createCategory({
      name,
      plan_id: currentPlan.id,
      current_month_plan_id: currentMonthPlan.id,
      parent_id: null,
      is_installment: false,
    });
    setShowAddGroupModal(false);
  };

  /**
   * Format month from YYYY-MM-DD to readable format
   */
  const formatMonth = (monthStr: string): string => {
    const date = new Date(monthStr);
    const monthKeys = ['january', 'february', 'march', 'april', 'may', 'june',
                       'july', 'august', 'september', 'october', 'november', 'december'];
    const monthName = t(`budget.months.${monthKeys[date.getMonth()]}`);
    return `${monthName} ${date.getFullYear()}`;
  };

  const handleMonthSelect = async (month: string) => {
    await loadMonthPlanByDate(month);
    setShowMonthPicker(false);
  };

  // Empty state - no plans exist
  if (!loading && !currentPlan) {
    return (
      <>
        <div
          className="min-h-screen bg-[#ffffeb] flex items-center justify-center px-4 py-6"
          style={{
            backgroundImage: `
              repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
              repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
            `
          }}
        >
          <div className="w-full max-w-md">
            <div
              className="bg-white border-4 border-[#077f5d] p-8"
              style={{ boxShadow: '6px 6px 0 #077f5d' }}
            >
              <div className="text-center">
                <div
                  className="w-20 h-20 bg-[#a9edba] border-4 border-[#077f5d] flex items-center justify-center mx-auto mb-6"
                  style={{ boxShadow: '4px 4px 0 #077f5d' }}
                >
                  <AlertCircle className="w-10 h-10 text-[#077f5d]" />
                </div>
                <h3
                  className="text-lg text-[#077f5d] mb-4 uppercase"
                  style={{ fontFamily: "'Press Start 2P', monospace", lineHeight: '1.4' }}
                >
                  {t('budget.no_book_title')}
                </h3>
                <p className="font-mono text-sm text-[#1a1a1a] mb-6">
                  {t('budget.no_book_subtitle')}
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="w-full bg-[#3cbe8c] text-white px-6 py-3 border-2 border-[#077f5d] font-bold uppercase"
                  style={{
                    fontFamily: "'Press Start 2P', monospace",
                    fontSize: '10px',
                    boxShadow: '6px 6px 0 #077f5d'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                    e.currentTarget.style.transform = 'translate(-2px, -2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
                    e.currentTarget.style.transform = 'translate(0, 0)';
                  }}
                >
                  <Plus className="w-4 h-4 inline mr-2" />
                  {t('budget.create_book')}
                </button>
              </div>
            </div>
          </div>
        </div>
        <PlanModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreatePlan}
          mode="create"
        />
      </>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#ffffeb] pb-24 lg:pb-8"
      style={{
        backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
          repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
        `
      }}
    >
      {/* Header with Plan and Month Selectors */}
      <Header
        leftContent={
          <>
            {/* Plan Name - Small Text */}
            <p className="text-sm font-mono opacity-90">
              {currentPlan?.name || t('budget.plan_fallback')}
            </p>

            {/* Month Selector - Medium Text with Press Start 2P */}
            {currentPlan && currentMonthPlan && (
              <button
                onClick={() => setShowMonthPicker(true)}
                className="text-left text-base font-bold uppercase hover:opacity-80 inline-block"
                style={{
                  fontFamily: "'Press Start 2P', monospace",
                  transition: 'none',
                  fontSize: '12px',
                }}
              >
                {formatMonth(currentMonthPlan.month)}<ChevronDownCircle size={14} strokeWidth={3} className="text-white inline-block align-text-bottom ml-1" />
              </button>
            )}
          </>
        }
        rightContent={
          <button
            onClick={handleAddGroup}
            className="flex items-center p-2 bg-[#3abf8c] text-white border-2 border-[#077f5d] font-bold uppercase"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '8px',
              boxShadow: '4px 4px 0 #077f5d',
              transition: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
          >
            <Plus className="pr-2" size={24} />
            {t('budget.add_group')}
          </button>
        }
      />

      {/* Main Content - Hierarchical Categories */}
      <div className="px-4 py-4 max-w-7xl mx-auto">
        <CategoryList />
      </div>

      {/* Add Group Modal */}
      <CategoryModal
        isOpen={showAddGroupModal}
        onClose={() => setShowAddGroupModal(false)}
        onSave={handleSaveGroup}
        category={null}
        title={t('budget.add_group_modal_title')}
      />

      {/* Month Calendar Picker Modal */}
      {showMonthPicker && currentMonthPlan && (
        <MonthCalendarPicker
          availableMonths={allMonthPlans.map(mp => mp.month)}
          currentMonth={currentMonthPlan.month}
          onSelectMonth={handleMonthSelect}
          onClose={() => setShowMonthPicker(false)}
        />
      )}
    </div>
  );
}
